import { FC } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Button,
  Label,
  Types,
  IconCollection,
  IconCollectionAlignment,
  IconCollectionVariation,
} from '@marriott/mi-ui-library';
import { StyledBrandRibbon } from './BrandRibbon.styles';
import { BrandRibbonVariation } from './BrandRibbon.types';
import type { LinkLists, BrandRibbonProps } from './BrandRibbon.types';

export const BrandRibbonComp: FC<BrandRibbonProps> = ({
  brandLogoTag,
  categorylists,
  componentId,
  ctaLink,
  forwardedRefs,
  logoLinkText,
  trackingProperties,
  variations = BrandRibbonVariation.PortfolioCollection,
}: BrandRibbonProps) => {
  const customAttributes = trackingProperties?.enableScrollingBehavior ? { 'data-section-tracking': componentId } : {};

  return (
    <StyledBrandRibbon
      data-component-name="o-common-static-brandribbon"
      id={componentId}
      data-testid="brand-ribbon"
      className={clsx({ 'brand-ribbon-extra-pt': Boolean(brandLogoTag) })}
      {...customAttributes}
    >
      {variations === BrandRibbonVariation?.PortfolioCollection ? (
        <div className="brands-ribbon-inner-container">
          <div className="brands-ribbon-logo" data-testid="brand-ribbon-header">
            <Button isLink href={ctaLink} className={brandLogoTag} ariaLabel={logoLinkText}>
              <span className="sr-only">{logoLinkText}</span>
            </Button>
          </div>
          {categorylists?.map((list: LinkLists) => (
            <div className="row" key={list.categorylabel}>
              <Label
                labelText={list.categorylabel}
                element={Types.tags.h3}
                size={Types.size.extraSmall}
                customClass="category col-12 col-lg-1"
              />
              <div className="cardLinks col-12 col-lg-11" role="region">
                <IconCollection
                  variation={IconCollectionVariation.LogoLinks}
                  alignment={IconCollectionAlignment.Horizontal}
                  iconCollectionData={list.linklists}
                  trackingProperties={trackingProperties}
                  forwardedRefs={forwardedRefs}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="cardLinks navBrands" role="region" data-testid="portfolio-brands">
          <IconCollection
            enableLinkClsName={Boolean(BrandRibbonVariation.PortfolioBrands)}
            variation={IconCollectionVariation.LogoLinks}
            alignment={IconCollectionAlignment.Horizontal}
            iconCollectionData={categorylists?.[0].linklists}
            trackingProperties={trackingProperties}
            forwardedRefs={forwardedRefs}
          />
        </div>
      )}
    </StyledBrandRibbon>
  );
};

export const BrandRibbonConfig = {
  emptyLabel: 'BrandRibbon',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/brandribbon`,
};

const BrandRibbonContainer = (props: BrandRibbonProps) => {
  const { styleclass } = props.model || {};

  return styleclass?.includes('fullbleed') ? (
    <div className={styleclass}>
      <div className="container">
        <BrandRibbonComp {...props} />
      </div>
    </div>
  ) : (
    <div className={clsx('container', styleclass)}>
      <BrandRibbonComp {...props} />
    </div>
  );
};

export const BrandRibbon = (props: BrandRibbonProps) => (
  <div data-testid="brand-ribbon-org">
    {props.cqPath?.includes('experiencefragment') ? (
      <BrandRibbonContainer {...props} {...props.model} />
    ) : (
      <EditableComponent config={BrandRibbonConfig} {...props}>
        <BrandRibbonContainer {...props} />
      </EditableComponent>
    )}
  </div>
);
